import { useMemo } from 'react';
import type { CashList } from './CashListConfigurationContext';
import { useCashListConfiguration } from './CashListConfigurationContext';
import type { CashListSelectorRules } from './filterCashListsForSelector';
import { filterCashListsForSelector } from './filterCashListsForSelector';

/**
 * Returns the cash lists from the cash list configuration context filtered for
 * a selector UI. Centralises the rules introduced for the Caritas cash management
 * restrictions (ENG-2097–2100) so each selector site stays a one-liner.
 *
 * Callers that need to gate a rule on a feature flag should read the flag
 * themselves and pass a conditional `excludeConfigIds` / `onlyConfigIds`.
 *
 * @example
 * const hideOnFF = useFeatureFlagEnabled(FeatureFlag.HidePliantReferenceInLinkSelector);
 * const cashLists = useCashListsForSelector({
 *   requireFlags: ['manualReceiptLinking'],
 *   excludeConfigIds: hideOnFF ? ['pliantReferenceCashListId'] : undefined,
 * });
 */
export const useCashListsForSelector = (
  rules: CashListSelectorRules
): CashList[] => {
  const configuration = useCashListConfiguration();

  return useMemo(
    () =>
      filterCashListsForSelector(configuration.cashLists, configuration, rules),
    [configuration, rules]
  );
};
